import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { buildPageList, ELLIPSIS } from './buildPageList';

/**
 * docs/02-DESIGN-SYSTEM.md §4.2 and docs/06-FRONTEND-SPEC.md §5.5 — "Crawlable
 * `<a href="?page=N">` links, not buttons."
 *
 * This is a hard SEO requirement, not a style preference: the news archive is
 * ~121 pages of the university's history, and a crawler that cannot follow
 * pagination will never index page 2 onwards. `onClick` handlers on `<button>`
 * elements are invisible to it.
 *
 * The SPA still intercepts the click for client-side navigation — but the
 * `href` is real, so middle-click, "open in new tab" and every crawler work.
 */
export interface PaginationLabels {
  /** Accessible name for the nav landmark, e.g. "გვერდები". */
  nav: string;
  previous: string;
  next: string;
  /** Builds the label for a numbered link, e.g. (n) => `გვერდი ${n}`. */
  page: (page: number) => string;
}

export interface PaginationProps {
  currentPage: number;
  lastPage: number;
  /** Builds the href for a page — the caller owns the query string. */
  buildHref: (page: number) => string;
  labels: PaginationLabels;
  /** Numbered links either side of the current page. */
  siblings?: number;
  className?: string;
}

const LINK_CLASSES =
  'inline-flex h-11 min-w-11 items-center justify-center rounded-full px-3 ' +
  'text-body-sm font-semibold no-underline transition-colors duration-fast';

export function Pagination({
  currentPage,
  lastPage,
  buildHref,
  labels,
  siblings = 1,
  className,
}: PaginationProps) {
  if (lastPage <= 1) {
    return null;
  }

  const pages = buildPageList(currentPage, lastPage, siblings);
  const hasPrevious = currentPage > 1;
  const hasNext = currentPage < lastPage;

  return (
    <nav aria-label={labels.nav} className={cn('flex justify-center', className)}>
      <ul className="flex flex-wrap items-center gap-2">
        <li>
          {hasPrevious ? (
            <a
              href={buildHref(currentPage - 1)}
              rel="prev"
              aria-label={labels.previous}
              className={cn(LINK_CLASSES, 'text-primary hover:bg-surface-alt')}
            >
              <Icon as={ChevronLeft} size="sm" />
            </a>
          ) : (
            <span
              aria-hidden="true"
              className={cn(LINK_CLASSES, 'cursor-not-allowed text-muted opacity-50')}
            >
              <Icon as={ChevronLeft} size="sm" />
            </span>
          )}
        </li>

        {pages.map((page, index) =>
          page === ELLIPSIS ? (
            <li key={`gap-${index}`} aria-hidden="true" className="px-1 text-secondary">
              {'…'}
            </li>
          ) : (
            <li key={page}>
              <a
                href={buildHref(page)}
                aria-label={labels.page(page)}
                aria-current={page === currentPage ? 'page' : undefined}
                className={cn(
                  LINK_CLASSES,
                  page === currentPage
                    ? 'bg-navy-600 text-white'
                    : 'text-primary hover:bg-surface-alt',
                )}
              >
                {page}
              </a>
            </li>
          ),
        )}

        <li>
          {hasNext ? (
            <a
              href={buildHref(currentPage + 1)}
              rel="next"
              aria-label={labels.next}
              className={cn(LINK_CLASSES, 'text-primary hover:bg-surface-alt')}
            >
              <Icon as={ChevronRight} size="sm" />
            </a>
          ) : (
            <span
              aria-hidden="true"
              className={cn(LINK_CLASSES, 'cursor-not-allowed text-muted opacity-50')}
            >
              <Icon as={ChevronRight} size="sm" />
            </span>
          )}
        </li>
      </ul>
    </nav>
  );
}
